import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../../../api";

interface HotspotInfo {
  index: number; // Correct position (1, 2, 3...)
  id_internal: number;
  explanation?: string;
  scale?: number;
  font_size?: number;
  rotation_x?: number;
  rotation_y?: number;
  rotation_z?: number;
}

interface UserAnswer {
  id_internal: number;
  index: number; // User's position
}

interface ZoneSortContentData {
  hotspot_info: HotspotInfo[];
  question?: string;
}

interface ZoneSortDetailProps {
  data: {
    list_hotspot_zone_sort?: UserAnswer[];
  };
  contentId?: number;
}

const ZoneSortDetail = ({ data, contentId }: ZoneSortDetailProps) => {
  const { t } = useTranslation();
  const [contentData, setContentData] = useState<ZoneSortContentData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedContentIdRef = useRef<number | null>(null);

  const userAnswers = data.list_hotspot_zone_sort || [];

  // Fetch content-zone-sort data
  useEffect(() => {
    const fetchData = async () => {
      if (!contentId) return;

      // Prevent duplicate fetches
      if (fetchedContentIdRef.current === contentId) return;
      fetchedContentIdRef.current = contentId;

      setIsLoading(true);

      try {
        const response = await axios.get(
          `${apiUrls.base}/api/content-zone-sorts?filters[content][id][$eq]=${contentId}`,
        );

        const results = response.data?.data || response.data;
        const sortData = Array.isArray(results) ? results[0] : results;

        if (sortData) {
          const attrs = sortData.attributes || sortData;
          setContentData({
            hotspot_info: attrs.hotspot_info || [],
            question: attrs.question || attrs.name,
          });
        }
      } catch (err) {
        console.error("Error fetching zone sort content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [contentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!contentData || contentData.hotspot_info.length === 0) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  // Correct position = id_internal + 1 (from SCORM: "zone (correct selection order): {k.id_internal + 1}")
  // hotspot.id_internal = identifier AND determines correct position
  // specific_data.index = user's chosen position (1-based)
  // specific_data.id_internal = identifier to match with hotspot.id_internal

  // Get user's position for a given hotspot (matched by hotspot.id_internal === userAnswer.id_internal)
  const getUserPosition = (hotspotIdInternal: number): number | null => {
    const userAnswer = userAnswers.find((a) => a.id_internal === hotspotIdInternal);
    if (!userAnswer) return null;
    return userAnswer.index > 0 ? userAnswer.index : null;
  };

  // Sort hotspots by correct position (id_internal + 1) for display
  const sortedHotspots = [...contentData.hotspot_info].sort(
    (a, b) => a.id_internal - b.id_internal,
  );

  // Check if all answers are correct (correct position = id_internal + 1)
  const allCorrect = sortedHotspots.every((hotspot) => {
    const userPos = getUserPosition(hotspot.id_internal);
    const correctPos = hotspot.id_internal + 1;
    return userPos === correctPos;
  });

  return (
    <div className="h-full flex flex-col">
      <div className="border-2 border-teal-200 rounded-lg p-4 flex-1 overflow-y-auto">
        {/* Column header for correct answer */}
        <div className="flex items-center gap-3 px-4 py-2 text-xs text-gray-500 uppercase tracking-wide">
          <span className="flex-1" />
          <span className="flex-shrink-0">{t("statsNew.interaction.quizSort.correctAnswer")}</span>
        </div>

        {/* Zones list - sorted by correct position (id_internal + 1) */}
        <div className="space-y-2">
          {sortedHotspots.map((hotspot) => {
            const userPosition = getUserPosition(hotspot.id_internal);
            const correctPosition = hotspot.id_internal + 1;
            const isCorrect = userPosition === correctPosition;
            const zoneLabel = hotspot.explanation || `Zone ${correctPosition}`;

            return (
              <div
                key={hotspot.id_internal}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                  isCorrect ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
                }`}
              >
                {/* Status icon */}
                <div className="flex-shrink-0">
                  {isCorrect ? (
                    <svg
                      className="h-5 w-5 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </div>

                {/* User's position (left) */}
                <span
                  className={`flex-shrink-0 font-bold ${
                    isCorrect ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {userPosition ?? "-"}
                </span>

                {/* Zone label/explanation */}
                <span className="flex-1 text-sm text-gray-900">{zoneLabel}</span>

                {/* Correct position (right) */}
                <span className="flex-shrink-0 text-sm text-gray-500 italic">
                  {correctPosition}
                </span>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            {allCorrect ? (
              <>
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                  <svg
                    className="h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span className="text-sm text-green-700 font-medium">
                  {t("statsNew.interaction.zoneSort.allCorrect")}
                </span>
              </>
            ) : (
              <>
                <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                  <svg
                    className="h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <span className="text-sm text-red-700 font-medium">
                  {t("statsNew.interaction.zoneSort.someIncorrect", {
                    correct: sortedHotspots.filter(
                      (h) => getUserPosition(h.id_internal) === h.id_internal + 1,
                    ).length,
                    total: sortedHotspots.length,
                  })}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoneSortDetail;
